/**
 * The eld (ISO 639-1) codes that count as each `LANGUAGE` classifier code. eld's `sr` model is Cyrillic
 * only, so Latin-script Serbian reads as `hr`.
 */
export const LANGUAGE_CODE_TO_ELD: Record<string, string[]> = {
    ALB: ['sq'],
    BUL: ['bg'],
    CZE: ['cs'],
    ENG: ['en'],
    ESP: ['es'],
    EST: ['et'],
    FIN: ['fi'],
    GER: ['de'],
    HUN: ['hu'],
    ITA: ['it'],
    LAV: ['lv'],
    LIT: ['lt'],
    POL: ['pl'],
    POR: ['pt'],
    SRP: ['sr', 'hr'],
    UKR: ['uk'],
};

/** Below this, text in the right language too often reads reliably as another one. */
const MIN_DETECTABLE_LENGTH = 15;

/**
 * How far the timecard language may trail eld's top score and still count as the language written. Short
 * entries and close relatives (Czech/Slovak, Spanish/Portuguese, Bulgarian/Russian) land just under the top.
 */
const MATCH_MARGIN = 0.23;

/**
 * The eld code of the language eld reliably reads the description as, when it is clearly ahead of the timecard
 * language; null otherwise, and always for a timecard language outside the map.
 */
export const mismatchedDescriptionLanguage = async (
    description: string | null | undefined,
    timecardLanguageCode: string | null | undefined
): Promise<string | null> => {
    const text = description?.trim() ?? '';
    const accepted = timecardLanguageCode ? LANGUAGE_CODE_TO_ELD[timecardLanguageCode] : undefined;
    if (!accepted || text.length < MIN_DETECTABLE_LENGTH) {
        return null;
    }

    // Loaded on first use: the n-gram database is large and the timecard forms sit in the main bundle.
    const { eld } = await import('eld/extrasmall');
    const result = eld.detect(text);
    if (!result.isReliable()) {
        return null;
    }
    const scores = result.getScores();
    const timecardLanguageScore = Math.max(0, ...accepted.map((code) => scores[code] ?? 0));

    return scores[result.language] - timecardLanguageScore > MATCH_MARGIN ? result.language : null;
};

/** True when the description reads as another language than the timecard's: see `mismatchedDescriptionLanguage`. */
export const isDescriptionLanguageMismatch = async (
    description: string | null | undefined,
    timecardLanguageCode: string | null | undefined
): Promise<boolean> => (await mismatchedDescriptionLanguage(description, timecardLanguageCode)) !== null;
